/**
 * adjustmentDecisionPdf — "VENDIM" për një rregullim page: një faqe A4, zyrtare.
 *
 * ── Pse dokument e jo tabelë ────────────────────────────────────────────────
 * `utils/pdfPrint.js` nxjerr raporte me kolona; një vendim nuk është raport. Ka
 * palë (kompania dhe punonjësi), objekt (shuma dhe periudha), dispozitiv
 * ("aprovohet"/"refuzohet"), arsyetim dhe nënshkrime. Prandaj ndërtohet si letër,
 * me të njëjtën paletë marke si regjistri i pagave, dhe me logon e KOMPANISË —
 * vendimi del në emrin e saj, jo të Nexuma-s.
 *
 * ── Një dokument, tri rrugë ────────────────────────────────────────────────
 * Shkarkimi, shtypja dhe bashkëngjitja në email vijnë nga i NJËJTI `buildDoc`.
 * Po të ndërtohej PDF-i i emailit veçmas (p.sh. te serveri), dy dokumente do të
 * mund të ndryshonin pa e vënë re askush — dhe ai që mban punonjësi në dorë do
 * të ishte i dyti.
 */
import { jsPDF } from 'jspdf';
import { drawCompanyLogo } from '../utils/payrollRegisterExport';
import { MONTHS_SQ } from '../utils/period';
import {
  CATEGORY_META, EVENT_LABELS, typeLabel, decisionRef, decisionFilename,
} from '../config/payrollAdjustments';

// Ri-eksportohen që thirrësit të kenë një adresë të vetme për dokumentin.
export { decisionRef, decisionFilename };

const INK    = [17, 24, 39];
const MUTED  = [107, 114, 128];
const FAINT  = [156, 163, 175];
const RULE   = [226, 232, 240];
const NAVY   = [48, 61, 80];        // #303D50 — koka e markës
const GREEN  = [21, 128, 61];
const RED    = [185, 28, 28];

const money = (v) => Number(v || 0).toLocaleString('sq-AL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Shenjat te PDF-ja shkruhen me ASCII.
 *
 * `CATEGORY_META.sign` mban minusin tipografik `−` (U+2212), i cili NUK është në
 * kodimin WinAnsi që përdor jsPDF-ja me fontet standarde: në letër dilte si `"`,
 * pra shuma e një avanci lexohej `" €777.00`. Shkronjat shqipe (ë, ç) janë brenda
 * WinAnsi-t dhe nuk preken.
 */
const asciiSign = (sign) => (sign === '−' ? '-' : (sign || ''));
const dt    = (d) => (d ? new Date(d).toLocaleDateString('sq-AL') : '—');
const dtm   = (d) => (d ? new Date(d).toLocaleString('sq-AL') : '—');
const periodOf = (r) => (r?.periodYear && r?.periodMonth ? `${MONTHS_SQ[r.periodMonth - 1]} ${r.periodYear}` : '—');

/**
 * @param {object} row      rregullimi, ashtu si vjen nga API-ja
 * @param {object} company  cilësimet (CompanyName, Address, FiscalNumber, Logo…)
 * @param {Array}  events   kronologjia; kur mungon, seksioni thjesht nuk vizatohet
 */
function buildDoc(row, company = {}, events = []) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210, H = 297, M = 18;
  const inner = W - M * 2;

  const rejected = row.status === 'rejected';
  const accent   = rejected ? RED : GREEN;
  const cat      = CATEGORY_META[row.category] || {};

  // ── Koka: marka e kompanisë ───────────────────────────────────────────────
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, W, 26, 'F');
  const logoW = drawCompanyLogo(doc, company.Logo, M, 7, 12, 34);
  const tx = M + (logoW ? logoW + 5 : 0);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12);
  doc.text(company.CompanyName || 'Kompania', tx, 13);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
  doc.setTextColor(190, 200, 210);
  const coLine = [company.Address, company.City, company.FiscalNumber && `NUI: ${company.FiscalNumber}`]
    .filter(Boolean).join('  ·  ');
  if (coLine) doc.text(coLine, tx, 18.5);
  doc.setFontSize(7);
  doc.text([company.Phone, company.Email].filter(Boolean).join('  ·  '), W - M, 18.5, { align: 'right' });

  // Vija e vendimit: jeshile kur aprovohet, e kuqe kur refuzohet — drejtimi
  // lexohet para se të lexohet fjala.
  doc.setFillColor(...accent);
  doc.rect(0, 26, W, 1.4, 'F');

  // ── Titulli ───────────────────────────────────────────────────────────────
  let y = 40;
  doc.setTextColor(...INK);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(17);
  doc.text('V E N D I M', W / 2, y, { align: 'center' });
  y += 6.5;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5);
  doc.setTextColor(...accent);
  doc.text(
    rejected ? 'për refuzimin e kërkesës për rregullim page' : 'për aprovimin e rregullimit të pagës',
    W / 2, y, { align: 'center' },
  );
  y += 6;
  doc.setFontSize(8); doc.setTextColor(...MUTED);
  doc.text(`Nr. ${decisionRef(row)}          Data: ${dt(row.approvedAt || new Date())}`, W / 2, y, { align: 'center' });

  y += 7;
  doc.setDrawColor(...RULE); doc.setLineWidth(0.3);
  doc.line(M, y, W - M, y);
  y += 9;

  // ── Palët dhe objekti ─────────────────────────────────────────────────────
  /** Çift etiketë/vlerë në dy kolona — i njëjti ritëm në krejt faqen. */
  const pair = (label, value, col = 0, rowY = y) => {
    const x = M + col * (inner / 2);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(6.8); doc.setTextColor(...FAINT);
    doc.text(String(label).toUpperCase(), x, rowY);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(...INK);
    doc.text(String(value || '—'), x, rowY + 4.6, { maxWidth: inner / 2 - 4 });
  };

  doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
  doc.text('PUNONJËSI', M, y);
  y += 5;
  pair('Emri dhe mbiemri', row.workerName || '—', 0, y);
  pair('Pozita', row.workerPosition || '—', 1, y);
  y += 11;
  pair('Numri personal', row.workerNid || '—', 0, y);
  pair('Nr. i punonjësit', row.workerCode || '—', 1, y);
  y += 13;

  doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
  doc.text('OBJEKTI I VENDIMIT', M, y);
  y += 5;
  pair('Lloji i rregullimit', `${cat.singular || row.category} · ${typeLabel(row.category, row.type)}`, 0, y);
  pair('Shuma', `${asciiSign(cat.sign)}€${money(row.amount)}`, 1, y);
  y += 11;
  pair('Periudha e pagës', periodOf(row), 0, y);
  pair('Data efektive', dt(row.effectiveDate), 1, y);
  y += 13;

  // ── Dispozitivi ───────────────────────────────────────────────────────────
  // Kutia e vendimit: fjalia formale, jo vetëm një etiketë statusi. Ky është
  // rreshti që dikush do të citojë më vonë.
  const verb = rejected ? 'REFUZOHET' : 'APROVOHET';
  const bodyText = rejected
    ? `${verb} kërkesa e punonjësit ${row.workerName || ''} për ${(cat.singular || row.category).toLowerCase()} `
      + `në shumën prej €${money(row.amount)}, për periudhën e pagës ${periodOf(row)}.`
    : `${verb} ${(cat.singular || row.category).toLowerCase()} në shumën prej €${money(row.amount)} `
      + `për punonjësin ${row.workerName || ''}, e cila `
      + (row.category === 'reimbursement'
        ? 'paguhet veçmas dhe nuk i nënshtrohet tatimit.'
        : `përfshihet në përllogaritjen e pagës për periudhën ${periodOf(row)}.`);

  const bodyLines = doc.splitTextToSize(bodyText, inner - 10);
  const boxH = bodyLines.length * 5.2 + 11;
  doc.setFillColor(rejected ? 254 : 240, rejected ? 242 : 253, rejected ? 242 : 244);
  doc.setDrawColor(...accent); doc.setLineWidth(0.4);
  doc.roundedRect(M, y, inner, boxH, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10.5); doc.setTextColor(...accent);
  doc.text(bodyLines, M + 5, y + 8);
  y += boxH + 9;

  // ── Arsyetimi ─────────────────────────────────────────────────────────────
  const section = (label, text, muted = false) => {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
    doc.text(label, M, y);
    y += 5;
    doc.setFont('helvetica', muted ? 'italic' : 'normal'); doc.setFontSize(9);
    doc.setTextColor(...(muted ? FAINT : INK));
    const lines = doc.splitTextToSize(text, inner);
    doc.text(lines, M, y);
    y += lines.length * 4.6 + 7;
  };

  section('ARSYETIMI I KËRKESËS', row.description || 'Pa arsyetim të dhënë', !row.description);
  if (rejected) {
    section('ARSYEJA E REFUZIMIT', row.rejectionReason || 'Pa arsye të regjistruar', !row.rejectionReason);
  }

  // ── Vlera e dokumentit ────────────────────────────────────────────────────
  // Një vendim duhet të thotë çka bën më tej, jo vetëm çka u vendos. Për të
  // refuzuarën kjo është e vetmja rrugë përpara që punonjësi ka.
  doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(...MUTED);
  const noteLines = doc.splitTextToSize(
    rejected
      ? 'Ky vendim i bashkëngjitet dosjes së punonjësit. Kërkesa mund të paraqitet sërish, e plotësuar sipas arsyes së mësipërme.'
      : 'Ky vendim i bashkëngjitet dosjes së punonjësit dhe shërben si dokument mbështetës për listën e pagës së periudhës përkatëse.',
    inner,
  );
  doc.text(noteLines, M, y);
  y += noteLines.length * 4 + 7;
  doc.setFont('helvetica', 'normal');

  // ── Rrjedha e procesit ────────────────────────────────────────────────────
  // Vendimi nuk qëndron pa rrugën që e solli: kush e kërkoi, kur u dërgua, kush
  // vendosi. Pa këtë, dokumenti do të ishte një pohim pa gjurmë.
  if (events && events.length) {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
    doc.text('RRJEDHA E PROCESIT', M, y);
    y += 5;
    doc.setFontSize(7.6);
    events.slice(-6).forEach((e) => {
      if (y > 232) return;                       // hapësira e nënshkrimeve nuk cenohet
      doc.setFont('helvetica', 'bold'); doc.setTextColor(...INK);
      doc.text(`• ${EVENT_LABELS[e.action] || e.action}`, M + 1, y);
      doc.setFont('helvetica', 'normal'); doc.setTextColor(...MUTED);
      doc.text(`${e.actorEmail || 'sistemi'} · ${dtm(e.createdAt)}`, M + 46, y);
      y += 4.4;
    });
    y += 4;
  }

  // ── Nënshkrimet ───────────────────────────────────────────────────────────
  const sigY = 250;
  doc.setDrawColor(...INK); doc.setLineWidth(0.3);
  doc.line(M, sigY, M + 62, sigY);
  doc.line(W - M - 62, sigY, W - M, sigY);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
  doc.text(row.approvedByName || 'Burimet Njerëzore', M, sigY + 4.5);
  doc.setTextColor(...FAINT); doc.setFontSize(6.8);
  doc.text('Personi që vendosi', M, sigY + 8.5);
  doc.setFontSize(7.5); doc.setTextColor(...MUTED);
  doc.text(row.workerName || '', W - M, sigY + 4.5, { align: 'right' });
  doc.setTextColor(...FAINT); doc.setFontSize(6.8);
  doc.text('Punonjësi (marrja në dijeni)', W - M, sigY + 8.5, { align: 'right' });

  doc.setFontSize(6.8); doc.setTextColor(...FAINT);
  doc.text('Vula:', W / 2 - 6, sigY + 4.5);
  doc.setDrawColor(...RULE);
  doc.roundedRect(W / 2 + 2, sigY - 2, 12, 9, 1, 1, 'S');

  // ── Fundi i faqes ─────────────────────────────────────────────────────────
  doc.setDrawColor(...RULE); doc.setLineWidth(0.3);
  doc.line(M, H - 16, W - M, H - 16);
  doc.setFontSize(6.5); doc.setTextColor(...FAINT);
  doc.text(
    `${company.CompanyName || ''} · Dokument konfidencial · Vendim nr. ${decisionRef(row)}`.replace(/^ · /, ''),
    M, H - 11.5,
  );
  doc.text(`Gjeneruar: ${dtm(new Date())}`, W - M, H - 11.5, { align: 'right' });
  if (row.status === 'processed' && row.salaryListName) {
    doc.setTextColor(...MUTED);
    doc.text(`Procesuar te "${row.salaryListName}".`, M, H - 7.5);
  }

  return doc;
}

export function downloadDecisionPdf(row, company, events) {
  buildDoc(row, company, events).save(decisionFilename(row));
}

/**
 * Hap dritaren e shtypjes mbi TË NJËJTIN dokument.
 *
 * `autoPrint` + blob-URL, jo një faqe HTML e dytë: ajo çka del nga printeri është
 * fjalë për fjalë PDF-ja që shkarkohet dhe që i shkon punonjësit me email.
 * Blob-i lirohet vonë me qëllim — Chrome-i e mbyll dialogun e shtypjes nëse
 * URL-ja revokohet para se shikuesi ta ketë ngarkuar.
 */
export function printDecisionPdf(row, company, events) {
  const doc = buildDoc(row, company, events);
  doc.autoPrint();
  const url = doc.output('bloburl');
  const win = window.open(url, '_blank');
  if (!win) {
    // Pop-up i bllokuar: më mirë një skedar në dorë sesa asgjë pa shpjegim.
    doc.save(decisionFilename(row));
    return false;
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return true;
}

export function decisionPdfBase64(row, company, events) {
  const bytes = new Uint8Array(buildDoc(row, company, events).output('arraybuffer'));
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}
